// @ts-strict-ignore
import React, { useMemo } from 'react'
import { Avatar, ComboboxItem } from '@node-space/ui-components'
import { useGetCustomersWalletProfileQuery } from 'hooks/queries/Customers/useCustomerWalletProfilesQuery'
import { useFeatureFlags } from 'hooks/useFeatureFlags'
import { CUSTOMER_WALLET_CURRENCY_PAYMENT_METHODS } from 'pages/Customers/constants'
import { stringArrayToFriendlyText } from 'utils/utils'
import { WalletProfileMapping } from '../types'
import { getOptions } from '../utils'

export const useFormOptions = (
  currency: string,
  isEmbeddedOrRelianceCustomerModel: boolean = false,
  customerId?: string
) => {
  const { enableEpWalletCurrencies } = useFeatureFlags()
  const {
    data: walletProfiles,
    isFetching: isFetchingWalletProfiles,
    error: walletProfilesError,
  } = useGetCustomersWalletProfileQuery({ customerId })

  const profileMappings: WalletProfileMapping = useMemo(() => {
    return (
      walletProfiles?.profiles?.reduce((currencyMappings: WalletProfileMapping, profile) => {
        const currencies = profile?.currencies
        const isCryptoProfile = profile?.id?.startsWith('crypto:')
        const isFiatProfile = profile?.id?.startsWith('fiat:')

        if (!isEmbeddedOrRelianceCustomerModel) {
          // if is EP
          if (
            (enableEpWalletCurrencies?.supported === 'crypto-only' && !isCryptoProfile) ||
            (enableEpWalletCurrencies?.supported === 'fiat-only' && !isFiatProfile)
          ) {
            return currencyMappings
          }
        }

        currencies?.forEach(currency => {
          if (!currencyMappings?.[currency]) {
            currencyMappings[currency] = {
              isCryptoProfile: isCryptoProfile,
              profiles: [],
            }
          }

          currencyMappings[currency].profiles.push({
            reference: profile?.id,
            paymentMethods: profile?.methods,
          })
        })

        return currencyMappings
      }, {}) ?? {}
    )
  }, [walletProfiles, isEmbeddedOrRelianceCustomerModel, enableEpWalletCurrencies])

  const walletProfileOptions: ComboboxItem[] = useMemo(() => {
    if (!profileMappings || !currency) return []

    const currencyProfileMapping = profileMappings?.[currency]
    if (!currencyProfileMapping?.profiles) return []

    return currencyProfileMapping.profiles
      .map(profile => {
        if (currencyProfileMapping.isCryptoProfile) {
          return {
            leftText: '',
            value: profile?.reference,
          }
        }

        const allowedMethods = CUSTOMER_WALLET_CURRENCY_PAYMENT_METHODS[currency] ?? []

        const currencyPaymentMethods = profile?.paymentMethods?.filter(paymentMethod =>
          allowedMethods.includes(paymentMethod?.toUpperCase())
        )

        const options = getOptions(currencyPaymentMethods)?.sort((a, b) => a.localeCompare(b))
        const label = stringArrayToFriendlyText(options)

        return {
          leftText: label,
          value: profile?.reference,
        }
      })
      .filter(option => currencyProfileMapping.isCryptoProfile || option.leftText)
      .sort((a, b) => a.leftText.localeCompare(b.leftText))
  }, [profileMappings, currency])

  const availableCurrencies = useMemo(() => {
    if (!profileMappings || isFetchingWalletProfiles) return []

    const currencies = Object.keys(profileMappings || {})
    const sortedCurrencies = currencies.toSorted((a, b) => a.localeCompare(b))

    return sortedCurrencies.map(currency => ({
      LeftElement: <Avatar name={currency?.toLowerCase()} size="small" />,
      leftText: currency,
      value: currency,
    }))
  }, [profileMappings, isFetchingWalletProfiles])

  return {
    availableCurrencies,
    walletProfileOptions,
    isFetchingWalletProfiles,
    walletProfilesError,
    profileMappings,
  }
}
